/**
 * What this store sells, as the storefront sees it (B11-P3). Pure: built from
 * the product-type registry, the published listings and the store's active
 * categories (Listing.storefront). Routes are /shop, /shop/<type slug> and
 * /p/<listing id>; laptop-era /laptops links redirect.
 */
import { modelAttributes } from '@/lib/productTypes';

/** A listing's product type key; null when it has none (the server never sends one: ADR 0009 §1). */
export const typeKeyOf = (l) => (l && l.product_type) || null;
export const attrsOf = (l) => modelAttributes(l || {});

/** Href helpers, one place for every link. */
export const shopHref = (type, params) => {
  const base = type ? `/shop/${type.slug}` : '/shop';
  const qs = params ? new URLSearchParams(params).toString() : '';
  return qs ? `${base}?${qs}` : base;
};
export const productHref = (l) => `/p/${l.id}`;

export function buildShop(types = [], listings = [], categories = []) {
  const byKey = Object.fromEntries(types.map((t) => [t.key, t]));
  const bySlug = Object.fromEntries(types.map((t) => [t.slug, t]));
  const counts = new Map();
  for (const l of listings) if (byKey[typeKeyOf(l)]) counts.set(typeKeyOf(l), (counts.get(typeKeyOf(l)) || 0) + 1);
  const inStock = types.filter((t) => counts.get(t.key)).map((t) => ({ ...t, count: counts.get(t.key) }));
  const catCount = new Map();
  for (const l of listings) if (l.category_id != null) catCount.set(String(l.category_id), (catCount.get(String(l.category_id)) || 0) + 1);
  const cats = categories.map((c) => ({ ...c, count: catCount.get(String(c.id)) || 0, type: byKey[c.product_type] || null }));
  // How the store talks about its stock: its one type's plural ("laptops"), else "products".
  const only = inStock.length === 1 ? inStock[0] : null;
  const noun = only ? only.plural.toLowerCase() : 'products';
  return {
    types,
    byKey,
    bySlug,
    inStock,
    categories: cats,
    categoriesWithStock: cats.filter((c) => c.count > 0),
    single: only,
    noun,
    typeOf: (l) => byKey[typeKeyOf(l)] || null,
    categoryById: (id) => cats.find((c) => String(c.id) === String(id)) || null,
  };
}

/**
 * Shop entries for the header nav and the home category tiles:
 *   a single-type store without its own categories, whose type declares
 *   collections (registry `collections`), shows "All …" and those collections
 *   (a laptop store: Business, Gaming, MacBooks…); otherwise "All …", one entry
 *   per product type in stock (when there are several) and the store's
 *   categories that have stock. [{ id, label, to, hint, icon }]
 */
export function shopEntries(shop) {
  const entries = [];
  const single = shop.single;
  const collections = (single && Array.isArray(single.collections) && single.collections) || [];
  if (collections.length && !shop.categoriesWithStock.length) {
    entries.push({ id: 'all', label: `All ${single.plural.toLowerCase()}`, to: shopHref(single), icon: 'layout-grid', hint: '' });
    for (const c of collections) entries.push({ id: c.id, label: c.label, to: shopHref(single, { cat: c.id }), icon: c.icon, hint: c.hint });
    return entries;
  }
  entries.push({ id: 'all', label: single ? `All ${single.plural.toLowerCase()}` : 'All products', to: single ? shopHref(single) : '/shop', icon: 'layout-grid', hint: '' });
  if (!single) for (const t of shop.inStock) entries.push({ id: `type-${t.key}`, label: t.plural, to: shopHref(t), icon: t.icon, hint: `${t.count} in stock` });
  for (const c of shop.categoriesWithStock) {
    if (c.type) entries.push({ id: `cat-${c.id}`, label: c.name, to: shopHref(c.type, { category: String(c.id) }), icon: c.type.icon, hint: `${c.count} in stock` });
  }
  return entries;
}
